import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { type AnimationSequence, useAnimate, useInView } from "framer-motion";
import { Flex, type BoxProps } from "@chakra-ui/react";
import { Tooltip } from "./tooltip";
import { DestinationMenu } from "./destination-menu";

const fadeIn = { opacity: 1 };
const fadeOut = { opacity: 0 };
const fast = { duration: 0.15 };
const iconTransition = { at: "-0.25", duration: 0.25 };
const resetTransition = { at: "-0.1", duration: 0.1 };

function useSyncFunnelsAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? ([
                // Animation sequence
                [
                  "#funnels-tooltip",
                  { scale: 1, ...fadeIn },
                  { at: "+0.8", type: "spring", damping: 30, stiffness: 800 },
                ],
                ["#funnels-tooltip-link", { color: "#40e398" }, { at: "+0.9", ...fast }],
                ["#funnels-tooltip-chevron", { x: "3px" }, { at: "-0.2", ...fast }],
                [
                  "#funnels-tooltip",
                  { scale: 0.9, ...fadeOut },
                  { at: "+0.6", type: "spring", damping: 30, stiffness: 500 },
                ],
                ["#funnels-menu", { scale: 1, ...fadeIn }, { type: "spring", damping: 30, stiffness: 800 }],
                ["#funnels-menu-spinner", { at: "-0.6", opacity: [0, 1, 0] }, { duration: 1.5 }],
                ["#funnels-fb--mono", fadeOut, { duration: 0.25 }],
                ["#funnels-fb--color", fadeIn, iconTransition],
                ["#funnels-ga--color", fadeIn, iconTransition],
                ["#funnels-ga--mono", fadeOut, iconTransition],
                ["#funnels-tiktok--color", fadeIn, iconTransition],
                ["#funnels-tiktok--mono", fadeOut, iconTransition],
                ["#funnels-check-svg", fadeIn, { at: "-0.2", type: "spring", damping: 30, stiffness: 800 }],
              ] as AnimationSequence)
            : ([
                // Reset
                ["#funnels-tooltip", { scale: 0.9, ...fadeOut }, { duration: 0.1 }],
                ["#funnels-tooltip-link", { color: "#dbe1e8" }, resetTransition],
                ["#funnels-tooltip-chevron", { x: 0 }, resetTransition],
                ["#funnels-menu", { scale: 0.9, ...fadeOut }, resetTransition],
                ["#funnels-menu-spinner", fadeOut, resetTransition],
                ["#funnels-check-svg", fadeOut, resetTransition],
                ["#funnels-fb--color", fadeOut, resetTransition],
                ["#funnels-fb--mono", fadeIn, resetTransition],
                ["#funnels-ga--color", fadeOut, resetTransition],
                ["#funnels-ga--mono", fadeIn, resetTransition],
                ["#funnels-tiktok--color", fadeOut, resetTransition],
                ["#funnels-tiktok--mono", fadeIn, resetTransition],
              ] as AnimationSequence),
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

export function SyncFunnelsAnimation(props: BoxProps) {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useSyncFunnelsAnimation(animationActive);
  const isInView = useInView(scope, { amount: 0.5 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex ref={scope} fontFamily="inter" align="center" w="50%" {...props}>
      <Tooltip
        id="funnels-tooltip"
        pos="absolute"
        opacity={0}
        top={{ base: -4, lg: 0 }}
        right={{ base: -3, lg: 0 }}
        transform="scale(0.9)"
      />

      <DestinationMenu
        id="funnels-menu"
        pos="absolute"
        opacity={0}
        top={-6}
        right={{ base: -6, md: -3, lg: -16 }}
        transform="scale(0.9)"
      />
    </Flex>
  );
}
